import { Component, OnInit, OnDestroy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatIconRegistry } from '@angular/material/icon';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { DomSanitizer } from '@angular/platform-browser';
import { Store } from '@ngrx/store';
import { ReplaySubject, Subject } from 'rxjs';
import { filter, pairwise, takeUntil } from 'rxjs/operators';

import { LaunchDarklyFlags } from 'app/core/services/launch-darkly/launch-darkly.types';
import { coreSelectors, getFeatureFlagObservable } from 'app/core/state';
import { formatUserName, prettyPlayerId } from 'app/core/utilities/user-util';
import { CurrentView } from 'app/shared/modals/add-livestream-modal/add-livestream-modal.types';
import assertDefined from 'app/utils/assert-defined';
import { Loggable } from 'app/utils/logging/loggable';
import { isUndefined, notUndefined } from 'app/utils/stream-util';
import User from 'entity/User';
import { Livestreams, Presentation, Presenter, UpdateType } from 'messages/websocket.messages';

import { ModalDialogData } from '../../../shared/modals/add-livestream-modal/add-livestream-modal.component';
import { ModalDialogComponent } from '../modal-dialog/modal-dialog.component';
import { LivestreamService } from '../services/livestream/livestream.service';
import { PresentationService } from '../services/presentation/presentation.service';
import { SceneService } from '../services/scene.service';
import { socialAppSelectors } from '../state/app';

const INVALID_USERID = 'invalid';

/**
 * @deprecated since version presenterV1.0
 * DO NOT DELETE, nessasary managing presenters via action menu
 */
@Component({
  selector: 'app-presentation-modal',
  templateUrl: './presentation-modal.component.html',
  styleUrls: ['./presentation-modal.component.scss'],
  standalone: false,
})
export class PresentationModalComponent extends Loggable implements OnInit, OnDestroy {
  public closed: Subject<boolean> = new Subject<boolean>();
  public participants: User[] = [];
  public participantsFiltered: User[] = [];
  public search = '';
  public previousPresenters: User[] = [];
  public previousRequestedPresenters: User[] = [];
  public selectedPresenters: User[] = [];
  public removedPresenters: Record<string, boolean> = {};
  public isConnectedRoom = false;
  public crossRoomPresentationEnabled?: boolean;
  public liveStreamManagementEnabled?: boolean;
  public presentationInProgress = false;
  public submitDisabled = false;
  public submitButtonText = 'Start Presentation';
  protected isSomeoneElsePresenting = false;

  private user?: User;
  protected isCrossRoom = false;
  private destroyed$ = new ReplaySubject<boolean>(1);
  public presentation?: Presentation;

  // Livestream
  public isLivestreamActive = false;
  public isPresentationFull = false;
  public selectedLivestream: Record<string, Presenter> = {};
  private presenters: Record<string, Presenter> = {};
  public livestreamPresenters: Presenter[] = [];

  public constructor(
    private sceneService: SceneService,
    private presentationService: PresentationService,
    private store: Store,
    private domSanitizer: DomSanitizer,
    private matIconRegistry: MatIconRegistry,
    private dialog: MatDialog,
    private livestreamService: LivestreamService,
  ) {
    super();
    this.setDisplayName('PresentationModalComponent');

    this.matIconRegistry?.addSvgIcon(
      'help-icon',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../../../../assets/images/help-icon.svg'),
    );

    this.store
      .select(socialAppSelectors.isConnectedRoom)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isConnectedRoom) => (this.isConnectedRoom = isConnectedRoom));

    this.store
      .select(coreSelectors.getUserEntity)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((user) => {
        this.user = user;
      });

    this.store
      .select(socialAppSelectors.isPresentationActiveInAnotherRoom)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isPresentationActiveInAnotherRoom) => {
        this.isSomeoneElsePresenting = isPresentationActiveInAnotherRoom;
      });

    this.store
      .select(socialAppSelectors.getPresentation)
      .pipe(
        pairwise(),
        filter(([prev, curr]) => isUndefined(prev) && notUndefined(curr)),
        takeUntil(this.destroyed$),
      )
      .subscribe(this.handlePresentationStart.bind(this));

    this.store
      .select(socialAppSelectors.isLivestreamActive)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isLivestreamActive) => (this.isLivestreamActive = isLivestreamActive));

    this.store
      .select(socialAppSelectors.isPresentationFull)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isPresentationFull) => (this.isPresentationFull = isPresentationFull));

    this.store
      .select(socialAppSelectors.getPresentation)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((presentation) => {
        this.presentation = presentation;
        this.presenters = presentation?.presenters ?? {};
      });

    this.store
      .select(socialAppSelectors.getQueuedLivestreams)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((livestreams) => {
        const queue = livestreams.queuedLivestreams ?? {};
        if (Object.keys(queue).length === 1) {
          this.selectedLivestream = queue;
        }
        this.updateSubmitButton();
      });

    this.store
      .select(socialAppSelectors.getActiveLivestreams)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((streams) => {
        this.livestreamPresenters = streams ?? [];
      });

    getFeatureFlagObservable(this.store, LaunchDarklyFlags.social_cross_room_presentation)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((crossRoomPresentationEnabled) => (this.crossRoomPresentationEnabled = crossRoomPresentationEnabled));

    getFeatureFlagObservable(this.store, LaunchDarklyFlags.social_live_stream_management)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((liveStreamManagementEnabled) => (this.liveStreamManagementEnabled = liveStreamManagementEnabled));
  }

  public ngOnInit(): void {
    this.participants = this.sceneService.getAllUsers();
    this.participantsFiltered = [...this.participants];
    this.sceneService.sceneManager?.participantsCountChange.pipe(takeUntil(this.destroyed$)).subscribe(() => {
      this.participants = this.sceneService.getAllUsers();
      this.participantsFiltered = [...this.participants];
      this.selectedPresenters = this.selectedPresenters.filter((presenter) =>
        this.participants?.find((participant) => participant.userId === presenter.userId),
      );
      this.onSearchChanged();
    });

    this.store
      .select(socialAppSelectors.getPresentation)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: async (presentation) => {
          if (!presentation) {
            this.previousPresenters = [];
            this.previousRequestedPresenters = [];
            this.presentationInProgress = false;
            this.updateSubmitButton();
            return;
          }
          this.presentationInProgress = true;
          const { presenters, requestedPresenterIds } = presentation;
          assertDefined(this.user?.userId);
          const { userId } = this.user;

          if (requestedPresenterIds.includes(userId)) {
            // Close the modal if you've been invited to present
            await this.close();
          }

          const presenterIds = Object.keys(presenters);
          this.previousPresenters = this.participants.filter((p: User) =>
            presenterIds.includes(p.userId ?? INVALID_USERID),
          );
          this.previousRequestedPresenters = this.participants.filter((p: User) =>
            requestedPresenterIds.includes(p.userId ?? INVALID_USERID),
          );
          this.selectedPresenters = this.participants.filter((p) => {
            assertDefined(p.userId);
            if (this.removedPresenters[p.userId]) {
              return false;
            }
            return presenterIds.includes(p.userId ?? '') || requestedPresenterIds.includes(p.userId ?? '');
          });
          this.updateSubmitButton();
        },
      });
  }

  public ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  public get showAddLivestream(): boolean {
    return (
      !!this.liveStreamManagementEnabled &&
      !this.isLivestreamActive &&
      !this.isPresentationFull &&
      Object.keys(this.selectedLivestream).length === 0
    );
  }

  private handlePresentationStart() {
    this.info('Another presentation started, dismissing modal');
    this.closed.next(true);
    const data = {
      title: 'Presentation Started by Another Participant',
      text: 'Another presentation has started.',
      primaryButtonText: 'OK',
    };
    this.dialog.open<ModalDialogComponent, ModalDialogData>(ModalDialogComponent, { data, width: '540px' });
  }

  private updateSubmitButton() {
    this.submitDisabled = this.presentationInProgress
      ? this.selectedPresenters.length > 4 // Prevent edge case if presentation changes while adding presenters, allow ending by removing all
      : this.selectedPresenters.length === 0 && Object.keys(this.selectedLivestream).length === 0; // Don't start presentation without any presenters
    this.submitButtonText = this.presentationInProgress ? 'Save presentation' : 'Start presentation';
  }

  public handleClickAddLivestream(): void {
    const view = CurrentView.SELECT_PRESENTERS;
    this.livestreamService.openAddLivestreamModal(view);
    this.updateSubmitButton();
  }

  public get notPresenters(): User[] {
    return (
      this.participantsFiltered?.filter(
        (p) => !this.selectedPresenters.find((presenter) => presenter?.userId === p?.userId),
      ) ?? []
    );
  }

  public async removeLivestream(livestream: Presenter): Promise<void> {
    const { userId: id } = livestream;
    this.selectedLivestream = {};
    const active = this.isLivestreamBroadcasting(id);
    if (active) {
      await this.livestreamService.removeActiveLivestream(livestream).catch((e) => this.error(e));
    } else {
      this.livestreamService.removeLivestreamFromQueue(id);
    }
    this.updateSubmitButton();
  }

  public isLivestreamBroadcasting(streamId: string): boolean {
    return !!this.presenters[streamId];
  }

  public getLivestreamTitle(livestream: Presenter): string {
    const title = 'Live: ' + (livestream?.firstName ?? 'Livestream');
    return title;
  }

  public getPresenterName(user: User): string {
    return formatUserName(user);
  }

  private isSelectedPerformer(participant: User): boolean {
    const findParticipant = this.selectedPresenters.find((element) => participant.userId === element.userId);
    return !!findParticipant;
  }

  public selectParticipant(participant: User): void {
    assertDefined(participant?.userId);
    const isSelectedParticipant = this.isSelectedPerformer(participant);
    assertDefined(participant.userId);
    if (!isSelectedParticipant) {
      this.selectedPresenters.push(participant);
      if (this.removedPresenters[participant.userId]) {
        this.removedPresenters = { ...this.removedPresenters, [participant.userId]: false };
      }
    } else {
      this.unselectPresenter(participant.userId);
    }
    this.updateSubmitButton();
  }

  public unselectPresenter(userId: string | undefined): void {
    if (userId) {
      const indexFound = this.selectedPresenters.findIndex((index) => index.userId === userId);
      this.removedPresenters = { ...this.removedPresenters, [userId]: true };
      this.selectedPresenters.splice(indexFound, 1);
      this.updateSubmitButton();
    }
  }

  public onSearchChanged(): void {
    this.search = this.search.trim();
    this.participantsFiltered = this.participants?.filter((p) => {
      const { firstName, lastName } = p;
      return (
        (firstName && firstName.toLowerCase().includes(this.search.toLowerCase())) ||
        (lastName && lastName.toLowerCase().includes(this.search.toLowerCase()))
      );
    });
  }

  /**
   * Toggle cross room presentation
   */
  public onCrossRoomToggle($event: MatSlideToggleChange): void {
    this.isCrossRoom = $event.checked;
  }

  public async close() {
    // Remove livestream from queue if closed
    const livestream: Presenter | undefined = Object.values(this.selectedLivestream)[0];
    if (livestream) {
      await this.removeLivestream(livestream);
    }
    this.closed.next(true);
  }

  private publishLivestream(livestream: Livestreams): void {
    const key = Object.keys(livestream)[0];
    const value = Object.values(livestream)[0];
    this.livestreamService.addActiveLivestream(
      {
        userId: key,
        isGuest: false,
        description: value.description,
        firstName: value.firstName,
        presenterType: value.presenterType,
        socialId: value.socialId,
      },
      this.isCrossRoom,
    );
  }

  /**
   * Initiate the presentation
   */
  public async onStartPresentation() {
    const { isCrossRoom, selectedPresenters } = this;
    const loggablePresenters = selectedPresenters.map(prettyPlayerId);
    this.info('Initiating presentation', { isCrossRoom, loggablePresenters });

    if (Object.keys(this.selectedLivestream).length) {
      const livestream = this.formatLivestream(this.selectedLivestream);
      this.presentationService
        .startPresentation({
          selectedPresenters: this.selectedPresenters,
          activePresenters: livestream,
          isCrossRoom,
        })
        .catch((e) => {
          this.error('Error starting presentation', e);
        });
    } else {
      this.presentationService
        .startPresentation({
          selectedPresenters: this.selectedPresenters,
          isCrossRoom,
        })
        .catch((e) => {
          this.error('Error starting presentation', e);
        });
    }
    await this.close();
  }

  private formatLivestream(livestream: Livestreams): Record<string, Presenter> {
    const key = Object.keys(livestream)[0];
    const value = Object.values(livestream)[0];
    const l = {
      [key]: {
        userId: key,
        isGuest: false,
        description: value.description,
        firstName: value.firstName,
        presenterType: value.presenterType,
        socialId: value.socialId,
      },
    };
    return l;
  }

  /**
   * Update a presentation already in progress
   */
  public async onUpdatePresentation() {
    const { isCrossRoom, removedPresenters, selectedPresenters } = this;
    const loggablePresenters = selectedPresenters.map(prettyPlayerId);
    this.info('Initiating presentation', { isCrossRoom, loggablePresenters });
    if (this.selectedPresenters) {
      await this.presentationService.updatePresentation({
        selectedPresenters,
        removedPresenters,
        updateType: isCrossRoom ? UpdateType.ALL : UpdateType.CURRENT,
      });
    }

    if (Object.keys(this.selectedLivestream).length === 1) {
      this.publishLivestream(this.selectedLivestream);
    }
    await this.close();
  }
}
